<template>
  <ModalBase show-modal auto-h narrow @close-modal="close" class="gk-modal-invoice-details">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:pretitle>
          {{ $gettext('EDIT') }}
        </template>
        <template v-slot:title>
          {{ $gettext('Invoice details') }}
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <div v-if="invoice" class="step-1--options-wrap">
        <div class="step-1--options gk-input-light--delete">

          <b-form-row>
            <b-col cols="12" lg="6">
              <!-- Project -->
              <div class="step-1--options__option">
                <div class="invoice-section--label">{{ $gettext('Project') }}</div>
                <ProjectSelect
                  ref="project-select"
                  :preselect="(preselectedProject) ? preselectedProject.project_id : null"
                  :customer-id="selectedCustomer.id"
                  allow-add searchable
                  @selected-project="setSelectedProject"
                  @add-project="addProject"
                />
                <div style="display: flex; justify-content: space-between">
                  <GekkoButton
                    @click="addProject('')"
                    :text="$gettext('Add new project')"
                    :icon="'fa-solid fa-plus'"
                    variant="link"
                    class="px-0"
                    size="sm"
                  />
                  <GekkoButton
                    v-if="(preselectedProject && preselectedProject.project_id) || showEditProject"
                    @click="editProject"
                    :text="$gettext('Edit project')"
                    :icon="'fa-regular fa-pen-to-square'"
                    variant="link"
                    class="px-0"
                    size="sm"
                  />
                </div>
              </div>
            </b-col>
            <b-col cols="12" lg="6">
              <!-- Project Reference -->
              <div class="step-1--options__option">
                <div class="invoice-section--label">{{ $gettext('Project Reference') }}</div>
                <b-form-input v-model="invoice.project_reference" :placeholder="$gettext('project reference')" />
              </div>
            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col cols="12" lg="6">

            </b-col>

            <b-col cols="12" lg="6">

            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col cols="12" lg="6">
              <!-- Number -->
              <div class="step-1--options__option">
                <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Quotation Number') }}</div>
                <div v-else class="invoice-section--label">{{ $gettext('Invoice Number') }}</div>
                <b-form-input v-model="invoice.number" :placeholder="$gettext('invoice number')" />
              </div>
            </b-col>
            <b-col cols="12" lg="6">
              <!-- Date -->
              <div class="step-1--options__option">
                <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Quotation Date') }}</div>
                <div v-else class="invoice-section--label">{{ $gettext('Invoice Date') }}</div>
                <b-form-datepicker id="input-1" v-model="invoice.invoice_date" no-flip initial-date="2021-01-01" :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }" />
              </div>
            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col cols="12" lg="12">
              <!-- payment options -->
              <div v-if="!invoice.paid_up_front" class="step-1--options__option">
                <div v-if="isQuotation" class="invoice-section--label">{{ $gettext('Valid for') }}</div>
                <div v-else class="invoice-section--label">{{ $gettext('Payment term') }}</div>
                <b-input-group>
                  <template #append>
                    <b-input-group-text>{{ $gettext('days') }}</b-input-group-text>
                  </template>
                  <b-form-input id="inv-payment_term" v-model="invoice.payment_term" :placeholder="(currentCompany.default_payment_term).toString()" />
                </b-input-group>
              </div>
              <!-- payment type selector (if paid upfront) -->
              <div v-else class="step-1--options__option">
                <div class="invoice-section--label">{{ $gettext('Payment type') }}</div>
                <b-form-select v-model="invoice.invoice_payment.payment_type" :options="paymentTypeOptions" />
              </div>
              <!-- paid upfront checkbox -->
              <div v-if="!isQuotation" class="step-1--options__option">
                <b-form-checkbox
                  id="checkbox-paidupfront"
                  v-model="invoice.paid_up_front"
                  name="checkbox-paidupfront"
                >
                  <span>{{ $gettext('Already paid') }}</span>
                </b-form-checkbox>
              </div>

              <!-- not euro paid upfront total_amount -->
              <div v-if="invoice.paid_up_front && invoice.currency != 'EUR'" class="step-1--options__option">
                <div class="invoice-section--label">{{ $gettext('Total in &euro;') }}</div>
                <b-form-input v-model="invoice.total_amount" :placeholder="$gettext('Invoice total in euro')" />
                <div v-if="invoice.errors && invoice.errors.total_amount" class="text-danger" style="font-size: 14px; margin-top: 0.5rem;">
                  {{ invoice.errors.total_amount }}
                </div>
              </div>

            </b-col>
          </b-form-row>

          <b-form-row>
            <b-col cols="12" lg="6">
              <!-- Currency -->
              <div v-if="!currentCompany.is_premium" @click="checkPremium('web_invoices_currency')" class="premium-blocker"></div>
              <div class="step-1--options__option">
                <div class="invoice-section--label">
                  <div class="premium-badge--inline">
                    {{ $gettext('Currency') }}
                    <GkBadge 
                      icon="fa-solid fa-crown"
                      icon-only
                      type="premium"
                    />
                  </div>
                </div>
                <CurrencyPicker v-model="invoice.currency" />
              </div>
            </b-col>
            <b-col cols="12" lg="6">
              <!-- Language -->
              <div v-if="!currentCompany.is_premium" @click="checkPremium('web_invoices_currency')" class="premium-blocker"></div>
              <div class="step-1--options__option">
                <div class="invoice-section--label">
                  <div class="premium-badge--inline">
                    {{ $gettext('Language') }}
                    <GkBadge 
                      icon="fa-solid fa-crown"
                      icon-only
                      type="premium"
                    />
                  </div>
                </div>
                <b-form-group label-class="gk-label-margin gk-modal-label">
                  <b-form-select :options="languageOptions" v-model="invoice.language" :required="true" :placeholder="$gettext('Dutch')" :disabled="!currentCompany.is_premium" />
                </b-form-group>
              </div>
            </b-col>
          </b-form-row>


        </div>
      </div><!-- end step-1--options-wrap -->
    </template>
    <template v-slot:actions>
      <GekkoButton
        @click="close()"
        :text="$gettext('Accept')"
      />
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import ModalBase from "@/components/base_components/ModalBase"
import TitleBar from "@/components/TitleBar"
import GekkoButton from "@/components/GekkoButton"

import CurrencyPicker from "@/components/CurrencyPicker"
import ProjectSelect from "@/components/invoices/ui/ProjectSelect"

export default {
  name: 'InvoiceDetailsModal',

  components: { 
    ModalBase,
    TitleBar,
    GekkoButton,
    CurrencyPicker,
    ProjectSelect
  },
  props: {
    invoice: {
      type: Object,
      default: null
    },
    selectedCustomer: {
      type: Object,
      default: null
    },
    preselectedProject: {
      type: Object,
      default: null
    },
    isQuotation: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      paymentTypeOptions: [
        { value: 'cash', text: this.$gettext('Cash or private account') },
        { value: 'bank', text: this.$gettext('Bank transaction') }
      ],
      showEditProject: false
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      languageOptions: "main/getInvoiceLanguageOptions",
      storeInvoice: "invoices/getInvoice"
    })
  },
  watch: {
    'invoice.invoice_date' (newDate) {
      this.setRecurrentInvoiceDate(newDate)
    }
  },
  created () {
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    ...mapMutations({ 
      setRecurrentInvoiceDate: 'invoicesRecurrent/SET_INVOICE_DATE'
    }),
    close () {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    addProject (projectName) {
      this.$emit('add-project-modal', projectName)
      this.$emit('close')
    },
    editProject () {
      this.$emit('edit-project-modal')
      this.$emit('close')
    },
    setSelectedProject (project) {
      this.$emit('select-project', project)
      if (project.id) {
        this.showEditProject = true
      } else {
        this.showEditProject = false
      }
    },
    checkPremium (premium_modal_type) {
      if (!this.currentCompany.is_premium) {
        this.$store.commit('togglePremiumModal', premium_modal_type)
      }
    }
  }
}
</script>

<style lang="scss">
.gk-modal-invoice-details {
  .gk-modal {
    max-width: 530px !important;
    .step-1--options__option {
      margin-bottom: 1rem;
    }
    .invoice-section--label {
      font-size: 1rem;
    }

    .action-buttons-wrapper {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
    }

    .premium-blocker {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 10;
    }
  }
}
</style>